<template>
  <div class="customer-card">
    <!-- Header -->
    <div class="tw-flex tw-items-center tw-justify-between tw-mb-4">
      <h3 class="tw-text-lg tw-text-gray-800 tw-font-normal">Customer Card</h3>
      <base-icon v-if="!isExpanded" @click="toggleExpand" class="tw-cursor-pointer tw-text-gray-500 hover:tw-text-gray-700">
        <chevron-down-icon class="tw-w-5 tw-h-5" />
      </base-icon>
      <base-icon v-else @click="toggleExpand" class="tw-cursor-pointer tw-text-gray-500 hover:tw-text-gray-700">
        <chevron-up-icon class="tw-w-5 tw-h-5" />
      </base-icon>
    </div>

    <!-- Warning Notice -->
    <div v-if="isExpanded" class="tw-mb-4 tw-p-3 tw-bg-yellow-50 tw-border tw-border-yellow-200 tw-rounded-md">
      <p class="tw-text-sm tw-text-gray-700">
        Please be aware that future charges that are more than
        <span class="tw-font-semibold tw-text-red-600">30 days</span> ahead <span class="tw-font-semibold tw-text-red-600">are not</span> always committed.
        <br />
        Please contact our Support Line at
        <a href="tel:770-568-6563" class="tw-text-primary-600 tw-underline">770-568-6563</a>
        for detailed information.
      </p>
    </div>

    <!-- Subscription ID -->
    <div v-if="isExpanded && subscriptionId" class="tw-mb-4">
      <p class="tw-text-sm tw-italic tw-text-gray-600">Subscription ID: {{ subscriptionId }}</p>
    </div>

    <!-- Loading State -->
    <div v-if="isLoading" class="tw-flex tw-justify-center tw-items-center tw-py-8"><span>Loading...</span></div>

    <!-- No Records State -->
    <div v-else-if="isExpanded && !hasRecords" class="tw-py-8 tw-text-center">
      <div class="tw-mb-3">
        <base-icon color="gray" size="xl">
          <calendar-icon class="tw-w-12 tw-h-12" />
        </base-icon>
      </div>
      <p class="tw-text-gray-500 tw-text-base tw-mb-1">No billing schedule found</p>
      <p class="tw-text-gray-400 tw-text-sm">
        Billing records will appear here once charges are processed
      </p>
    </div>

    <!-- Billing Sections -->
    <div v-else-if="isExpanded && hasRecords">
      <!-- Current Billing Cycle -->
      <div class="tw-mb-6">
        <h4 class="tw-text-md tw-text-gray-800 tw-mb-3 tw-border-b tw-pb-2">
          Current Billing Cycle
        </h4>
        <div v-if="currentBillingCycle">
          <div class="tw-flex tw-justify-between tw-items-center tw-py-2 tw-text-sm">
            <div>
              <span class="tw-text-green-600 ">
                {{ formatDate(currentBillingCycle.billingDate) }}
              </span>
              <span class="tw-text-gray-600 tw-ml-2">
                - Monthly Subscription Charge
              </span>
            </div>
            <span class="tw-text-gray-800 ">
              {{ formatCurrency(currentBillingCycle.paidPrice) }}
            </span>
          </div>
        </div>
        <div v-else class="tw-text-gray-500 tw-text-sm tw-py-2">
          No current billing cycle
        </div>
      </div>

      <!-- Future Billing Cycles -->
      <div class="tw-mb-6">
        <h4 class="tw-text-md  tw-text-gray-800 tw-mb-3 tw-border-b tw-pb-2">
          Future Billing Cycles
        </h4>
        <div v-if="futureBillingCycles.length > 0">
          <div
            v-for="(cycle, index) in futureBillingCycles"
            :key="cycle.id"
            class="tw-flex tw-justify-between tw-items-center tw-py-2 tw-text-sm"
            :class="{ 'tw-border-b tw-border-gray-100': index < futureBillingCycles.length - 1 }"
          >
            <div class="tw-text-sm">
              <span class="tw-text-blue-700">
                {{ formatDate(cycle.billingDate) }}
              </span>
              <span class="tw-text-gray-600 tw-ml-2">
                - Monthly Subscription Charge
              </span>
              <span v-if="index === 0" class="tw-text-gray-400  tw-italic tw-ml-2">
                (This is your Next Charge)
              </span>
            </div>
            <span class="tw-text-gray-800 ">
              {{ formatCurrency(cycle.originalPrice) }}
            </span>
          </div>
        </div>
        <div v-else class="tw-text-gray-500 tw-py-2">
          No future billing cycles scheduled
        </div>
      </div>

      <!-- Past Billing Cycles -->
      <div>
        <h4 class="tw-text-md tw-text-gray-800 tw-mb-3 tw-border-b tw-pb-2">
          Past Billing Cycles
        </h4>
        <div v-if="pastBillingCycles.length > 0">
          <div v-for="(cycle, index) in pastBillingCycles" :key="cycle.id" class="tw-py-2" :class="{ 'tw-border-b tw-border-gray-100': index < pastBillingCycles.length - 1 }">
            <div class="tw-flex tw-justify-between tw-items-center tw-text-sm">
              <div>
                <span class=" tw-text-lime-800">
                  {{ formatDate(cycle.billingDate) }}
                </span>
                <span class="tw-text-gray-600 tw-ml-2">
                  - Monthly Subscription Charge
                </span>
              </div>
              <span class="tw-text-gray-800 tw-text-sm">
                {{ formatCurrency(cycle.paidPrice) }}
              </span>
            </div>
            <!-- Failed Payment Info -->
            <div v-if="cycle.failedPaymentCount > 0" class="tw-mt-1 tw-text-sm tw-text-red-600">Failed attempts: {{ cycle.failedPaymentCount }}</div>
          </div>
        </div>
        <div v-else class="tw-text-gray-500 tw-text-sm tw-py-2">
          No past billing cycles
        </div>
        <!-- Activities Note -->
        <div v-if="completedSubmitActivitiesCount > 0" class="tw-mt-3 tw-text-sm tw-text-gray-600 tw-italic">{{ completedSubmitActivitiesCount }} Submit Activities Completed</div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import { ChevronDownIcon, ChevronUpIcon, CalendarIcon } from "@/assets/svgs";

export default {
  name: "CustomerCard",
  components: {
    ChevronDownIcon,
    ChevronUpIcon,
    CalendarIcon,
  },
  props: {
    businessId: {
      type: String,
      required: true,
    },
    userId: {
      type: String,
      required: true,
    },
    userBusinessPlanId: {
      type: String,
      required: true,
    },
    subscriptionId: {
      type: String,
      default: null,
    },
    currency: {
      type: String,
      default: "USD",
    },
    defaultExpanded: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      isExpanded: this.defaultExpanded,
      isLoading: false,
      hasRecords: false,
      billingSchedules: [],
      completedSubmitActivitiesCount: 0,
    };
  },
  computed: {
    locale() {
      return window.navigator.language;
    },
    currentBillingCycle() {
      const now = moment();
      return this.billingSchedules.find(
        (schedule) => schedule.status === "completed" && moment(schedule.billingDate).isSameOrBefore(now) && moment(schedule.billingDate).isAfter(now.clone().subtract(1, "month")),
      );
    },
    futureBillingCycles() {
      const now = moment();
      return this.billingSchedules
        .filter((schedule) => schedule.status === "pending" && moment(schedule.billingDate).isAfter(now))
        .sort((a, b) => moment(a.billingDate).diff(moment(b.billingDate)));
    },
    pastBillingCycles() {
      const now = moment();
      const currentCycleDate = this.currentBillingCycle.billingDate;

      return this.billingSchedules
        .filter((schedule) => {
          const billingDate = moment(schedule.billingDate);
          const isPast = billingDate.isBefore(now);
          const isNotCurrent = !currentCycleDate || !moment(schedule.billingDate).isSame(moment(currentCycleDate));

          return (schedule.status === "completed" || schedule.status === "failed") && isPast && isNotCurrent;
        })
        .sort((a, b) => moment(b.billingDate).diff(moment(a.billingDate)));
    },
  },
  mounted() {
    if (this.isExpanded) {
      this.loadBillingSchedule();
    }
  },
  methods: {
    toggleExpand() {
      this.isExpanded = !this.isExpanded;
      if (this.isExpanded && !this.hasRecords && this.billingSchedules.length === 0) {
        this.loadBillingSchedule();
      }
    },
    async loadBillingSchedule() {
      try {
        this.isLoading = true;
        const response = await this.$axios.get(`/businesses/${this.businessId}/members/${this.userId}/billing-schedule/${this.userBusinessPlanId}`);

        // if (response.data.hasRecords) {
        //   this.hasRecords = true;
        //   this.billingSchedules = response.data.schedules;
        // } else {
        //   this.hasRecords = false;
        //   this.billingSchedules = [];
        // }
        if (response.data.hasRecords) {
          this.hasRecords = true;
          this.billingSchedules = response.data.schedules;
          this.completedSubmitActivitiesCount = response.data.completedSubmitActivitiesCount || 0;
          console.log("Completed Submit Activities Count:", response.data.completedSubmitActivitiesCount);
        } else {
          this.hasRecords = false;
          this.billingSchedules = [];
          this.completedSubmitActivitiesCount = response.data.completedSubmitActivitiesCount || 0;
        }
      } catch (error) {
        this.EventBus.$emit("GLOBAL_ERROR", {
          title: `Unable to load billing schedule details`,
          message: `We were not able to retrieve the billing schedule data. There may have been a connection error. Please reload the page to try again.`,
          error,
        });
        this.hasRecords = false;
        this.billingSchedules = [];
      } finally {
        this.isLoading = false;
      }
    },

    formatDate(date) {
      if (!date) return "---";
      return moment(date).format("MMMM Do");
    },
    formatCurrency(cents) {
      if (!cents && cents !== 0) return "$0.00";
      return new Intl.NumberFormat(this.locale, {
        style: "currency",
        currency: this.currency,
      }).format(cents / 100);
    },
    refresh() {
      this.loadBillingSchedule();
    },
  },
};
</script>
